import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";

const sections = [
  {
    title: "1. Who We Are",
    content: (
      <>
        <p>
          TruthForge Limited ("TruthForge", "we", "us", "our") is a company registered in England and Wales. Our registered office is at The Circle, Queen Elizabeth Street, London, SE1 2JJ.
        </p>
        <p>
          We operate the website at <span className="text-foreground">www.truthforge.ai</span> (the "Site") and provide narrative intelligence and decision support services (the "Services").
        </p>
        <p>For the purposes of UK data protection law, TruthForge Limited is the <strong className="text-foreground">data controller</strong> of your personal data.</p>
        <p>If you have questions about how we handle your personal data, contact us at: <strong className="text-foreground">privacy@truthforge.ai</strong></p>
      </>
    ),
  },
  {
    title: "2. What Personal Data We Collect",
    content: (
      <>
        <p>We collect and process the following categories of personal data:</p>
        <p><strong className="text-foreground">Information you provide directly:</strong></p>
        <ul>
          <li>Full name</li>
          <li>Email address</li>
          <li>Organisation name</li>
          <li>Job title or role</li>
          <li>Description of your situation or enquiry</li>
          <li>The type of situation you are facing (e.g. litigation, regulatory matter, dispute)</li>
          <li>The urgency of your situation</li>
        </ul>
        <p><strong className="text-foreground">Information collected automatically when you visit the Site:</strong></p>
        <ul>
          <li>IP address</li>
          <li>Browser type and version</li>
          <li>Pages visited and time spent on those pages</li>
          <li>Referring URL</li>
          <li>Device type and operating system</li>
          <li>Cookie identifiers (see Section 8)</li>
        </ul>
        <p>We do not collect any special category data (such as health information, political opinions, or criminal records) through our website forms. If you choose to describe a situation in your enquiry that incidentally includes such information, we will treat it with the strictest confidence.</p>
      </>
    ),
  },
  {
    title: "3. How We Collect Personal Data",
    content: (
      <>
        <p>We collect personal data:</p>
        <ul>
          <li>When you submit an enquiry through the contact form on our Site</li>
          <li>When you request a Narrative Stress-Test or ask to discuss a situation</li>
          <li>When you email us directly</li>
          <li>Automatically through cookies and analytics tools when you browse the Site</li>
        </ul>
      </>
    ),
  },
  {
    title: "4. Why We Process Your Personal Data and Our Legal Basis",
    content: (
      <>
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr><th>Purpose</th><th>Legal Basis</th></tr>
            </thead>
            <tbody>
              <tr><td>To respond to your enquiry and assess whether we can assist you</td><td>Legitimate interests (Article 6(1)(f) UK GDPR), responding to prospective client enquiries is a core business activity</td></tr>
              <tr><td>To provide our Services once an engagement is agreed</td><td>Performance of a contract (Article 6(1)(b) UK GDPR)</td></tr>
              <tr><td>To maintain records of client and prospective client interactions</td><td>Legitimate interests, maintaining accurate business records</td></tr>
              <tr><td>To comply with legal obligations (e.g. anti-money laundering, record-keeping)</td><td>Legal obligation (Article 6(1)(c) UK GDPR)</td></tr>
              <tr><td>To improve our Site and Services through analytics</td><td>Legitimate interests, understanding how the Site is used helps us improve it</td></tr>
              <tr><td>To send you service-related communications</td><td>Legitimate interests or contract performance, as applicable</td></tr>
            </tbody>
          </table>
        </div>
        <p>We do not use your personal data for unsolicited marketing without your explicit consent.</p>
      </>
    ),
  },
  {
    title: "5. How Long We Keep Your Data",
    content: (
      <>
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr><th>Data Type</th><th>Retention Period</th></tr>
            </thead>
            <tbody>
              <tr><td>Enquiry form submissions (where no engagement follows)</td><td>12 months from receipt</td></tr>
              <tr><td>Client records (where an engagement proceeds)</td><td>7 years from the end of the engagement</td></tr>
              <tr><td>Website analytics data</td><td>26 months</td></tr>
              <tr><td>Cookie data</td><td>See Section 8</td></tr>
            </tbody>
          </table>
        </div>
        <p>After these periods, your data is securely deleted or anonymised.</p>
      </>
    ),
  },
  {
    title: "6. Who We Share Your Data With",
    content: (
      <>
        <p>We treat all enquiries and client information as strictly confidential. We do not sell, rent, or trade your personal data.</p>
        <p>We may share your data with:</p>
        <p><strong className="text-foreground">Service providers acting as data processors on our behalf</strong>, including:</p>
        <ul>
          <li>Our website hosting provider</li>
          <li>Our database and form processing provider</li>
          <li>Our email service provider</li>
          <li>Our analytics provider</li>
        </ul>
        <p>All processors are bound by data processing agreements and are required to handle your data only on our instructions.</p>
        <p><strong className="text-foreground">Legal and regulatory authorities</strong>, if we are required to disclose your data by law, court order, or regulatory requirement. Where permitted, we will notify you before doing so.</p>
        <p>We do not share your data with any third party for their own marketing purposes.</p>
      </>
    ),
  },
  {
    title: "7. International Transfers",
    content: (
      <>
        <p>Where we transfer personal data outside the UK or European Economic Area, we ensure appropriate safeguards are in place, including:</p>
        <ul>
          <li>Standard Contractual Clauses approved by the UK ICO or European Commission</li>
          <li>Transfers to countries with an adequacy decision</li>
        </ul>
      </>
    ),
  },
  {
    title: "8. Cookies",
    content: (
      <>
        <p>Our Site uses cookies to function and to help us understand how it is used.</p>
        <p><strong className="text-foreground">Strictly necessary cookies</strong>, required for the Site to operate. These cannot be disabled.</p>
        <p><strong className="text-foreground">Analytics cookies</strong>, help us understand how visitors interact with the Site (e.g. pages visited, time on site). These are only set with your consent.</p>
        <p>You can manage your cookie preferences at any time through the cookie settings on our Site, or by adjusting your browser settings. Disabling analytics cookies will not affect your ability to use the Site.</p>
      </>
    ),
  },
  {
    title: "9. Security",
    content: (
      <>
        <p>We take the security of your personal data seriously. We use appropriate technical and organisational measures to protect your data against unauthorised access, loss, or disclosure, including:</p>
        <ul>
          <li>Encrypted connections (HTTPS/TLS) across the Site</li>
          <li>Access controls on our systems and databases</li>
          <li>Regular security reviews</li>
        </ul>
        <p>No method of transmission over the internet is completely secure. While we take all reasonable steps to protect your data, we cannot guarantee absolute security.</p>
      </>
    ),
  },
  {
    title: "10. Your Rights",
    content: (
      <>
        <p>Under UK GDPR, you have the following rights in relation to your personal data:</p>
        <ul>
          <li><strong className="text-foreground">Right of access</strong>, to request a copy of the personal data we hold about you</li>
          <li><strong className="text-foreground">Right to rectification</strong>, to ask us to correct inaccurate or incomplete data</li>
          <li><strong className="text-foreground">Right to erasure</strong>, to ask us to delete your data in certain circumstances</li>
          <li><strong className="text-foreground">Right to restrict processing</strong>, to ask us to pause processing of your data in certain circumstances</li>
          <li><strong className="text-foreground">Right to data portability</strong>, to receive your data in a structured, machine-readable format</li>
          <li><strong className="text-foreground">Right to object</strong>, to object to processing based on legitimate interests</li>
          <li><strong className="text-foreground">Rights in relation to automated decision-making</strong>, we do not use automated decision-making or profiling that produces legal or similarly significant effects</li>
        </ul>
        <p>To exercise any of these rights, contact us at <strong className="text-foreground">privacy@truthforge.ai</strong>. We will respond within one calendar month.</p>
      </>
    ),
  },
  {
    title: "11. Right to Complain",
    content: (
      <>
        <p>If you believe we have not handled your personal data correctly, you have the right to lodge a complaint with the UK Information Commissioner's Office (ICO):</p>
        <p><strong className="text-foreground">Information Commissioner's Office</strong><br />Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF<br />Tel: 0303 123 1113<br />Website: <a href="https://ico.org.uk" target="_blank" rel="noreferrer">ico.org.uk</a></p>
        <p>We would appreciate the opportunity to address your concerns directly before you contact the ICO, please reach out to us first at <strong className="text-foreground">privacy@truthforge.ai</strong>.</p>
      </>
    ),
  },
  {
    title: "12. Changes to This Policy",
    content: <p>We may update this Privacy Policy from time to time. When we make material changes, we will update the "Last updated" date at the top of this page. We encourage you to review this Policy periodically.</p>,
  },
  {
    title: "13. Contact Us",
    content: (
      <p><strong className="text-foreground">TruthForge Limited</strong><br />The Circle, Queen Elizabeth Street<br />London, SE1 2JJ</p>
    ),
  },
];

const PrivacyPolicy = () => (
  <div className="min-h-screen bg-background text-foreground">
    <SEO
      title="Privacy Policy — TruthForge AI"
      description="How TruthForge Limited collects, processes, and protects personal data under UK data protection law."
      path="/privacy-policy"
    />
    <Navigation />
    <main className="mx-auto max-w-3xl px-5 py-12 md:px-8 md:py-16">
      <div className="mb-10 border-b border-border/70 pb-8">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">TruthForge Limited</p>
        <h1 className="text-3xl font-semibold tracking-normal text-foreground md:text-4xl">Privacy Policy</h1>
        <p className="mt-4 text-xs uppercase tracking-[0.1em] text-body">Last updated: 28 April 2026</p>
      </div>

      <div className="space-y-10 text-sm [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:tracking-normal [&_li]:pl-1 [&_li]:leading-7 [&_p]:leading-7 [&_p]:text-body [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-border/60 [&_td]:p-3 [&_td]:align-top [&_td]:leading-7 [&_td]:text-body [&_th]:border [&_th]:border-border/60 [&_th]:bg-surface [&_th]:p-3 [&_th]:text-left [&_th]:font-semibold [&_th]:leading-7 [&_th]:text-foreground [&_ul]:ml-5 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:text-body">
        {sections.map((section) => (
          <section key={section.title} className="space-y-4">
            <h2>{section.title}</h2>
            <div className="space-y-4">{section.content}</div>
          </section>
        ))}
      </div>
    </main>
    <Footer />
  </div>
);

export default PrivacyPolicy;